import { getClinicasRepository } from '../repositories';
import { ClinicaItem, EstadoItem, CidadeItem, BairroItem, ApiError, ExameClinicoItem } from './types';

export class ClinicaService {
  private static get repository() {
    return getClinicasRepository();
  }

  /**
   * Lista todas as clínicas disponíveis
   * @param isPropria - Filtrar apenas clínicas próprias (opcional, não implementado ainda)
   * @returns Promise com array de clínicas
   */
  static async getClinicas(isPropria?: boolean): Promise<ClinicaItem[]> {
    try {
      // Usar getClinicasFiltradas sem filtros para buscar todas as clínicas
      return await this.repository.getClinicasFiltradas({}) as any;
    } catch (error: any) {
      throw this.handleApiError(error);
    }
  }

  /**
   * Lista estados disponíveis (baseado nas clínicas)
   * @returns Promise com array de estados
   */
  static async getEstados(): Promise<EstadoItem[]> {
    try {
      return await this.repository.getEstados() as any;
    } catch (error: any) {
      throw this.handleApiError(error);
    }
  }

  /**
   * Lista cidades por estado
   * @param estado - UF do estado
   * @returns Promise com array de cidades
   */
  static async getCidadesByEstado(estado: string): Promise<CidadeItem[]> {
    try {
      return await this.repository.getCidadesByEstado(estado) as any;
    } catch (error: any) {
      throw this.handleApiError(error);
    }
  }

  /**
   * Lista bairros por cidade e estado
   * @param estado - UF do estado
   * @param cidade - Nome da cidade
   * @returns Promise com array de bairros
   */
  static async getBairrosByCidadeEstado(estado: string, cidade: string): Promise<BairroItem[]> {
    try {
      return await this.repository.getBairrosByCidadeEstado(estado, cidade) as any;
    } catch (error: any) {
      throw this.handleApiError(error);
    }
  }

  /**
   * Lista clínicas com filtros de localização
   * @param filters - Filtros opcionais (estado, cidade, bairro, limit, online)
   * @returns Promise com array de clínicas
   */
  static async getClinicasFiltradas(filters: {
    estado?: string;
    cidade?: string;
    bairro?: string;
    limit?: number;
    online?: boolean;
    lat?: number;
    lng?: number;
  } = {}): Promise<ClinicaItem[]> {
    try {
      return await this.repository.getClinicasFiltradas(filters) as any;
    } catch (error: any) {
      throw this.handleApiError(error);
    }
  }

  /**
   * Busca uma clínica específica por ID
   * @param id - ID da clínica
   * @returns Promise com dados da clínica
   */
  static async getClinicaById(id: number): Promise<ClinicaItem> {
    try {
      return await this.repository.getById(id) as any;
    } catch (error: any) {
      throw this.handleApiError(error);
    }
  }

  /**
   * Busca as próximas datas disponíveis para agendamento em uma clínica
   * @param clinicaId - ID da clínica
   * @param online - Se true, busca apenas datas com horários online
   * @returns Promise com array de datas disponíveis (formato DD/MM/YYYY)
   */
  static async getProximasDatasDisponiveis(clinicaId: number, online?: boolean): Promise<string[]> {
    try {
      return await this.repository.getProximasDatasDisponiveis(clinicaId, online) as any;
    } catch (error: any) {
      throw this.handleApiError(error);
    }
  }

  /**
   * Busca todos os exames clínicos disponíveis em uma clínica
   * @param clinicaId - ID da clínica
   * @returns Promise com array de exames clínicos
   */
  static async getExamesClinicosClinica(clinicaId: number): Promise<ExameClinicoItem[]> {
    try {
      return await this.repository.getExamesClinicosClinica(clinicaId) as any;
    } catch (error: any) {
      throw this.handleApiError(error);
    }
  }

  /**
   * Busca dados de uma clínica específica pelo ID
   * @param id - ID da clínica
   * @returns Promise com dados da clínica
   */
  static async getById(id: number): Promise<ClinicaItem> {
    try {
      return await this.repository.getById(id) as any;
    } catch (error: any) {
      throw this.handleApiError(error);
    }
  }

  /**
   * Trata erros da API
   * @param error - Erro da API
   * @throws Error com mensagem formatada
   */
  private static handleApiError(error: any): never {
    if (error.response) {
      const message = error.response.data?.message || error.response.data?.error || 'Erro ao buscar dados';
      throw new Error(message);
    }
    throw new Error('Erro de conexão com o servidor');
  }

  /**
   * Verifica se alguma clínica possui atendimento online configurado
   */
  static async checkHasOnline(): Promise<boolean> {
    try {
      const result = await this.repository.getHasOnline();
      return result.hasOnline;
    } catch (error: any) {
      console.error('Erro ignorado intencionalmente no checkHasOnline:', error);
      return false;
    }
  }

  /**
   * Atualiza as coordenadas geográficas da clínica em background
   */
  static async updateGeolocalizacao(id: number, coords: { lat?: number; lng?: number }): Promise<any> {
    try {
      return await this.repository.updateGeolocalizacao(id, coords);
    } catch (error: any) {
      console.error('Erro ignorado no updateGeolocalizacao:', error);
      return null;
    }
  }
}
